import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

export default function PortalTrainingQuizPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [loading, setLoading]     = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions]  = useState([]);
  const [material, setMaterial]    = useState(null);
  const [materialViewed, setMaterialViewed] = useState(false);
  const [error, setError]          = useState('');

  // Quiz state
  const [phase, setPhase]          = useState('intro'); // intro | material | quiz | result
  const [answers, setAnswers]       = useState({});     // { questionId: [selectedIds] }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [materialIdx, setMaterialIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);   // { score, passed, gradedResponses }
  const startedAtRef                = useRef(null);

  const hasMaterial = Array.isArray(material) && material.length > 0;

  useEffect(() => {
    portalService.getTrainingAssignment(id)
      .then(({ assignment, questions, material }) => {
        setAssignment(assignment);
        setQuestions(questions || []);
        setMaterial(material || null);
        setMaterialViewed(!!assignment.materialViewedAt);
        // If already passed and review is allowed, jump straight to result view with last attempt
        if (assignment.status === 'passed' && assignment.Version?.allowReview) {
          const lastAttempt = (assignment.Attempts || [])[0];
          if (lastAttempt) {
            setResult({ score: lastAttempt.score, passed: true, gradedResponses: lastAttempt.responsesJson });
            setPhase('result');
          }
        }
      })
      .catch(() => setError(t('Failed to load training.', 'Error al cargar la capacitación.', lang)))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const handleStart = async () => {
    try {
      await portalService.startTraining(id);
      startedAtRef.current = new Date().toISOString();
      setPhase('quiz');
      setCurrentIdx(0);
      setAnswers({});
    } catch (err) {
      setError(err?.response?.data?.message || t('Could not start training.', 'No se pudo iniciar la capacitación.', lang));
    }
  };

  const handleReviewMaterial = () => {
    setMaterialIdx(0);
    setPhase('material');
  };

  const handleMaterialDone = async () => {
    try {
      await portalService.markTrainingMaterialViewed(id);
      setMaterialViewed(true);
      await handleStart();
    } catch (err) {
      setError(err?.response?.data?.message || t('Could not record material review.', 'No se pudo registrar la revisión del material.', lang));
    }
  };

  const toggleAnswer = (questionId, optionId, type) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (type === 'single' || type === 'true_false') {
        return { ...prev, [questionId]: [optionId] };
      }
      const already = current.includes(optionId);
      return { ...prev, [questionId]: already ? current.filter(i => i !== optionId) : [...current, optionId] };
    });
  };

  const currentQuestion = questions[currentIdx];
  const currentAnswer   = answers[currentQuestion?.id] || [];
  const allAnswered      = questions.every(q => (answers[q.id] || []).length > 0);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const responses = questions.map(q => ({
        questionId: q.id,
        selectedIds: answers[q.id] || [],
      }));
      const data = await portalService.submitTraining(id, responses, startedAtRef.current);
      setResult(data);
      setPhase('result');
    } catch (err) {
      setError(err?.response?.data?.message || t('Submission failed.', 'Error al enviar.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">
        <div className="spinner-border spinner-border-sm me-2" />
        {t('Loading…', 'Cargando…', lang)}
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/portal/training')}>
          ← {t('Back', 'Regresar', lang)}
        </button>
      </div>
    );
  }

  // ── Intro screen ─────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    const version = assignment?.Version;
    const lastAttempt = (assignment?.Attempts || [])[0];
    const canRetry = assignment?.status === 'failed' &&
      (!version?.maxAttempts || (assignment?.attemptCount || 0) < version.maxAttempts);
    const isPassed = assignment?.status === 'passed';

    return (
      <div>
        <button className="btn btn-link btn-sm p-0 text-muted mb-3" onClick={() => navigate('/portal/training')}>
          ← {t('Back', 'Regresar', lang)}
        </button>
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-1">{assignment?.Training?.title}</h5>
            {assignment?.Training?.description && (
              <p className="text-muted" style={{ fontSize: 14 }}>{assignment.Training.description}</p>
            )}

            <div className="d-flex flex-wrap gap-3 my-3">
              {version?.estimatedMinutes && (
                <div className="text-center">
                  <div style={{ fontSize: 22 }}>⏱</div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>{version.estimatedMinutes} {t('min', 'min', lang)}</div>
                </div>
              )}
              <div className="text-center">
                <div style={{ fontSize: 22 }}>❓</div>
                <div style={{ fontSize: 12, color: '#6c757d' }}>{questions.length} {t('questions', 'preguntas', lang)}</div>
              </div>
              {version?.passingScore && (
                <div className="text-center">
                  <div style={{ fontSize: 22 }}>🎯</div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>{version.passingScore}% {t('to pass', 'para aprobar', lang)}</div>
                </div>
              )}
              {version?.maxAttempts && (
                <div className="text-center">
                  <div style={{ fontSize: 22 }}>🔄</div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>
                    {assignment.attemptCount || 0}/{version.maxAttempts} {t('attempts', 'intentos', lang)}
                  </div>
                </div>
              )}
            </div>

            {lastAttempt && (
              <div className={`alert ${lastAttempt.passed ? 'alert-success' : 'alert-warning'} py-2`} style={{ fontSize: 13 }}>
                {t('Last attempt', 'Último intento', lang)}: {lastAttempt.score}%
                {lastAttempt.passed ? ` ✓ ${t('Passed', 'Aprobado', lang)}` : ` ✗ ${t('Not passed', 'No aprobado', lang)}`}
              </div>
            )}

            {isPassed ? (
              <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
                ✓ {t('You have passed this training.', 'Has aprobado esta capacitación.', lang)}
              </div>
            ) : assignment?.status === 'expired' ? (
              <div className="alert alert-secondary py-2" style={{ fontSize: 13 }}>
                {t('This assignment has expired.', 'Esta asignación ha vencido.', lang)}
              </div>
            ) : canRetry || assignment?.status === 'pending' || assignment?.status === 'in_progress' ? (
              hasMaterial && !materialViewed ? (
                <button className="btn btn-primary w-100 mt-2" onClick={handleReviewMaterial}>
                  📖 {t('Review Material', 'Revisar Material', lang)}
                </button>
              ) : (
                <button className="btn btn-primary w-100 mt-2" onClick={handleStart}>
                  {assignment?.status === 'in_progress'
                    ? t('Continue Training', 'Continuar Capacitación', lang)
                    : assignment?.status === 'failed'
                    ? t('Retry Training', 'Reintentar Capacitación', lang)
                    : t('Start Training', 'Iniciar Capacitación', lang)}
                </button>
              )
            ) : (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                {t('No more attempts allowed.', 'No se permiten más intentos.', lang)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Material screen ───────────────────────────────────────────────────────────
  if (phase === 'material') {
    const slide = material[materialIdx];
    const isLastSlide = materialIdx === material.length - 1;
    const slideTitle = (lang === 'es' && slide.titleEs) ? slide.titleEs : slide.title;
    const slideBody  = (lang === 'es' && slide.bodyEs) ? slide.bodyEs : slide.body;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted" style={{ fontSize: 12 }}>
            📖 {t('Slide', 'Diapositiva', lang)} {materialIdx + 1} / {material.length}
          </span>
        </div>

        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar bg-info" style={{ width: `${Math.round(((materialIdx + 1) / material.length) * 100)}%` }} />
        </div>

        <div className="card shadow-sm mb-3">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3">{slideTitle}</h5>
            <p style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{slideBody}</p>
          </div>
        </div>

        <div className="d-flex justify-content-between gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setMaterialIdx(i => Math.max(0, i - 1))}
            disabled={materialIdx === 0}
          >
            ← {t('Previous', 'Anterior', lang)}
          </button>

          {!isLastSlide ? (
            <button className="btn btn-primary" onClick={() => setMaterialIdx(i => i + 1)}>
              {t('Next', 'Siguiente', lang)} →
            </button>
          ) : (
            <button className="btn btn-success" onClick={handleMaterialDone}>
              ✓ {t("I've reviewed this — Start Quiz", 'He revisado esto — Iniciar Cuestionario', lang)}
            </button>
          )}
        </div>

        <div className="d-flex flex-wrap gap-1 justify-content-center mt-3">
          {material.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setMaterialIdx(i)}
              style={{
                width: 28, height: 28, borderRadius: '50%', border: 'none',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: i === materialIdx ? '#0d6efd' : i < materialIdx ? '#198754' : '#e9ecef',
                color: i === materialIdx || i < materialIdx ? '#fff' : '#6c757d',
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Quiz screen ──────────────────────────────────────────────────────────────
  if (phase === 'quiz') {
    const progress = Math.round(((currentIdx + 1) / questions.length) * 100);

    return (
      <div>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted" style={{ fontSize: 12 }}>
            {t('Question', 'Pregunta', lang)} {currentIdx + 1} / {questions.length}
          </span>
          <span className="text-muted" style={{ fontSize: 12 }}>
            {t('answered', 'respondidas', lang)}: {Object.keys(answers).length}/{questions.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Question card */}
        {currentQuestion && (
          <div className="card shadow-sm mb-3">
            <div className="card-body p-4">
              <p className="fw-semibold mb-3" style={{ fontSize: 16, lineHeight: 1.5 }}>
                {(lang === 'es' && currentQuestion.textEs) ? currentQuestion.textEs : currentQuestion.text}
              </p>

              {currentQuestion.type === 'multi' && (
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  {t('Select all that apply', 'Selecciona todas las que apliquen', lang)}
                </div>
              )}

              <div className="d-flex flex-column gap-2">
                {(currentQuestion.options || []).map(opt => {
                  const selected = currentAnswer.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleAnswer(currentQuestion.id, opt.id, currentQuestion.type)}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px',
                        borderRadius: 10,
                        border: selected ? '2px solid #0d6efd' : '2px solid #dee2e6',
                        background: selected ? '#e8f0fe' : '#fff',
                        color: selected ? '#0d6efd' : '#212529',
                        fontWeight: selected ? 600 : 400,
                        fontSize: 14,
                        transition: 'all 0.15s',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ marginRight: 8 }}>
                        {selected ? '●' : '○'}
                      </span>
                      {(lang === 'es' && opt.textEs) ? opt.textEs : opt.text}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="d-flex justify-content-between gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
          >
            ← {t('Previous', 'Anterior', lang)}
          </button>

          {currentIdx < questions.length - 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => setCurrentIdx(i => i + 1)}
            >
              {t('Next', 'Siguiente', lang)} →
            </button>
          ) : (
            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={submitting || !allAnswered}
            >
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-2" />{t('Submitting…', 'Enviando…', lang)}</>
                : t('Submit', 'Enviar', lang)}
            </button>
          )}
        </div>

        {/* Question dots */}
        <div className="d-flex flex-wrap gap-1 justify-content-center mt-3">
          {questions.map((q, i) => (
            <button
              key={q.id}
              onClick={() => setCurrentIdx(i)}
              style={{
                width: 28, height: 28, borderRadius: '50%', border: 'none',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: i === currentIdx ? '#0d6efd' : answers[q.id]?.length ? '#198754' : '#e9ecef',
                color: i === currentIdx || answers[q.id]?.length ? '#fff' : '#6c757d',
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {!allAnswered && currentIdx === questions.length - 1 && (
          <div className="text-center text-muted mt-2" style={{ fontSize: 12 }}>
            {t('Answer all questions before submitting.', 'Responde todas las preguntas antes de enviar.', lang)}
          </div>
        )}
      </div>
    );
  }

  // ── Result screen ─────────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    const passed = result.passed;
    const version = assignment?.Version;

    return (
      <div>
        {/* Score card */}
        <div
          className="card shadow-sm mb-4 text-center"
          style={{ borderTop: `6px solid ${passed ? '#198754' : '#dc3545'}` }}
        >
          <div className="card-body p-4">
            <div style={{ fontSize: 52 }}>{passed ? '🎉' : '😔'}</div>
            <h4 className="fw-bold mt-2">
              {passed
                ? t('You Passed!', '¡Aprobaste!', lang)
                : t('Not Passed', 'No Aprobado', lang)}
            </h4>
            <div style={{ fontSize: 40, fontWeight: 700, color: passed ? '#198754' : '#dc3545' }}>
              {result.score}%
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              {t('Passing score', 'Puntaje de aprobación', lang)}: {version?.passingScore ?? 80}%
            </div>

            {!passed && (
              <div className="mt-3">
                {version?.maxAttempts && (assignment?.attemptCount || 0) >= version.maxAttempts ? (
                  <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                    {t('No more attempts allowed.', 'No se permiten más intentos.', lang)}
                  </div>
                ) : (
                  <button className="btn btn-outline-primary mt-2" onClick={() => { setPhase('intro'); setResult(null); }}>
                    🔄 {t('Try Again', 'Intentar de Nuevo', lang)}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Review answers */}
        {result.gradedResponses?.length > 0 && (
          <div>
            <div className="fw-semibold mb-2" style={{ fontSize: 14 }}>
              {t('Answer Review', 'Revisión de Respuestas', lang)}
            </div>
            {questions.map((q, idx) => {
              const graded = result.gradedResponses.find(r => r.questionId === q.id);
              const isCorrect = graded?.correct;
              return (
                <div
                  key={q.id}
                  className="card mb-3"
                  style={{ borderLeft: `4px solid ${isCorrect ? '#198754' : '#dc3545'}` }}
                >
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className="fw-semibold" style={{ fontSize: 14 }}>
                        {idx + 1}. {(lang === 'es' && q.textEs) ? q.textEs : q.text}
                      </span>
                      <span style={{ fontSize: 18, flexShrink: 0, marginLeft: 8 }}>
                        {isCorrect ? '✅' : '❌'}
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      {(q.options || []).map(opt => {
                        const wasSelected = (graded?.selectedIds || []).includes(opt.id);
                        return (
                          <div
                            key={opt.id}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 6,
                              fontSize: 13,
                              background: wasSelected
                                ? isCorrect ? '#e9f7ef' : '#fdecea'
                                : 'transparent',
                              color: wasSelected
                                ? isCorrect ? '#198754' : '#dc3545'
                                : '#6c757d',
                              fontWeight: wasSelected ? 600 : 400,
                            }}
                          >
                            {wasSelected ? (isCorrect ? '✓ ' : '✗ ') : '  '}
                            {(lang === 'es' && opt.textEs) ? opt.textEs : opt.text}
                          </div>
                        );
                      })}
                    </div>
                    {(q.explanation || q.explanationEs) && (
                      <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: 12, color: '#495057' }}>
                        💡 {(lang === 'es' && q.explanationEs) ? q.explanationEs : q.explanation}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button className="btn btn-outline-secondary w-100 mt-2" onClick={() => navigate('/portal/training')}>
          ← {t('Back to Trainings', 'Volver a Capacitaciones', lang)}
        </button>
      </div>
    );
  }

  return null;
}
